import React, { useState, useEffect } from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import '../../create_event/create_event.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { youtubeRegex } from '../../../../global/http_enum';
import { showAlert } from '../../../../functions/show_alert';
import MapLocationDialog from '../../../posters/poster_wizard/country_map/map_location_dialog';

export default function WizardEventForm({ projectId, onDataChange, initialData }) {
    const [eventType, setEventType] = useState(initialData?.eventType || 'Theory');
    const [file, setFile] = useState(initialData?.file || null);
    const [youtubeLink, setYoutubeLink] = useState(initialData?.youtubeLink || '');
    const [showMap, setShowMap] = useState(false);
    const [eventName, setEventName] = useState(initialData?.eventName || '');
    const [eventDesc, setEventDesc] = useState(initialData?.eventDesc || '');
    const [negative, setNegative] = useState(initialData?.negative || '');
    const [problem, setProblem] = useState(initialData?.problem || '');
    const [eventDate, setEventDate] = useState(initialData?.eventDate || '');
    const [clickedLocation, setClickedLocation] = useState(initialData?.location || null);
    const [location, setLocation] = useState(initialData?.location || '');
    const [uploadedFileName, setUploadedFileName] = useState(initialData?.uploadedFileName || null);

    useEffect(() => {
        if (clickedLocation != null) {
            setLocation(clickedLocation);
        }
    }, [clickedLocation]);

    // Notify parent of data changes
    useEffect(() => {
        if (onDataChange) {
            onDataChange({
                eventType,
                eventName,
                eventDesc,
                location,
                negative,
                problem,
                eventDate,
                file,
                youtubeLink,
                uploadedFileName
            });
        }
    }, [eventType, eventName, eventDesc, location, negative, problem, eventDate, file, youtubeLink, uploadedFileName, onDataChange]);

    const handleFileUpload = (pickedFile) => {
        if (pickedFile.size > 10000000) {
            showAlert(false, 'the file Size is More then 10 MB');
            return;
        }
        if (pickedFile) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = btoa(reader.result);
                setFile(base64String);
                setUploadedFileName(pickedFile.name);
            };
            reader.readAsBinaryString(pickedFile);
        }
    };

    const handleSaveLocation = (locationData) => {
        if (locationData && locationData.address) {
            setLocation(locationData.address);
            setClickedLocation(locationData.address);
        }
        setShowMap(false);
    };

    const handleShowMap = () => {
        setShowMap(true);
    };

    return (
        <div className="wizard-event-form">
            <div className="domain-pills">
                <button
                    className={`domain-pill ${eventType === 'Theory' ? 'active' : ''}`}
                    onClick={() => setEventType('Theory')}
                >
                    Theory Domain
                </button>
                <button
                    className={`domain-pill ${eventType === 'Communication' ? 'active' : ''}`}
                    onClick={() => setEventType('Communication')}
                >
                    Communication Domain
                </button>
            </div>

            <div className="form-container">
                <div className="form-field field-order-1">
                    <label className="field-label">Event Name*</label>
                    <input
                        type="text"
                        className="form-input"
                        placeholder="Type here"
                        value={eventName}
                        onChange={(e) => setEventName(e.target.value)}
                    />
                </div>

                <div className="form-field field-order-2">
                    <label className="field-label">Event Location*</label>
                    <div className="location-input-wrapper">
                        <input
                            type="text"
                            className="form-input disabled-input"
                            placeholder="Select location from Event Map"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            disabled={true}
                        />
                        <LocationOnIcon className="location-icon" />
                    </div>
                </div>

                <div className="form-field field-order-3">
                    <label className="field-label">Event Date*</label>
                    <div className="date-input-wrapper">
                        <input
                            type="date"
                            id="event-date-input"
                            className="form-input date-input"
                            placeholder="MM/DD/YYYY"
                            value={eventDate}
                            onChange={(e) => setEventDate(e.target.value)}
                        />
                        <CalendarTodayIcon
                            className="calendar-icon"
                            onClick={() => {
                                const dateInput = document.getElementById('event-date-input');
                                if (dateInput) {
                                    dateInput.focus();
                                    if (dateInput.showPicker) {
                                        dateInput.showPicker();
                                    } else {
                                        dateInput.click();
                                    }
                                }
                            }}
                        />
                    </div>
                </div>

                <div className="form-field field-order-4">
                    <label className="field-label">Negative Function Executed*</label>
                    <input
                        type="text"
                        className="form-input"
                        placeholder="Type here"
                        value={negative}
                        onChange={(e) => setNegative(e.target.value)}
                    />
                </div>

                <div className="form-field field-order-5">
                    <label className="field-label">Problem Identified from Event*</label>
                    <input
                        type="text"
                        className="form-input"
                        placeholder="Type here"
                        value={problem}
                        onChange={(e) => setProblem(e.target.value)}
                    />
                </div>

                <div className="form-field field-order-6">
                    <label className="field-label">Event Description*</label>
                    <ReactQuill
                        className="problem-quill"
                        theme="snow"
                        value={eventDesc}
                        onChange={setEventDesc}
                        placeholder="Type here"
                    />
                </div>
            </div>

            <div className="media-section">
                <div className="media-row">
                    <div className="form-field youtube-field">
                        <label className="field-label">Youtube Link</label>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="Insert link"
                            value={youtubeLink}
                            onChange={(e) => setYoutubeLink(e.target.value)}
                        />
                    </div>

                    <div className="form-field event-map-field">
                        <label className="field-label">Event Map</label>
                        <button className="event-map-button" onClick={handleShowMap}>
                            Click to Show Map
                            <LocationOnIcon className="event-map-icon" />
                        </button>
                    </div>
                </div>

                <div className="form-field">
                    <label className="field-label">Upload Files</label>
                    <div
                        className={`file-upload-area ${uploadedFileName ? 'has-file' : ''}`}
                        onClick={() => {
                            document.getElementById('wizard-file-input').click();
                        }}
                    >
                        <input
                            id="wizard-file-input"
                            type="file"
                            style={{ display: 'none' }}
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    handleFileUpload(e.target.files[0]);
                                }
                            }}
                        />
                        <CloudUploadIcon className="upload-icon" />
                        <p className="upload-text">Upload the file here</p>
                        <p className="upload-hint">(Only .jpg, .png, & .pdf files will be accepted)</p>
                        {uploadedFileName && (
                            <p className="uploaded-file">{uploadedFileName}</p>
                        )}
                    </div>
                    {!uploadedFileName && (
                        <p className="no-files-text">no files uploaded yet</p>
                    )}
                </div>
            </div>

            <MapLocationDialog
                isOpen={showMap}
                onClose={() => setShowMap(false)}
                onSaveLocation={handleSaveLocation}
                initialLocation={location || 'USA'}
            />
        </div>
    );
}

// Helper function to strip HTML tags and check if content is empty
const stripHtmlAndCheckEmpty = (htmlString) => {
    if (!htmlString) return true;
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.trim() === '' || textContent.trim() === '<p><br></p>' || htmlString.trim() === '<p><br></p>';
};

// Export validate function helper
export const validateEventForm = (eventData) => {
    if (!eventData.eventName || eventData.eventName.trim() === '') {
        return { valid: false, message: 'Event name is Required' };
    }
    if (!eventData.eventDesc || stripHtmlAndCheckEmpty(eventData.eventDesc)) {
        return { valid: false, message: 'Event Description is Required' };
    }
    if (!eventData.location || eventData.location.trim() === '') {
        return { valid: false, message: 'Event Location is Required' };
    }
    if (!eventData.negative || eventData.negative.trim() === '') {
        return { valid: false, message: 'Negative Function Executed is Required' };
    }
    if (!eventData.problem || eventData.problem.trim() === '') {
        return { valid: false, message: 'Problem Identified from Event is Required' };
    }
    if (!eventData.eventDate || eventData.eventDate.trim() === '') {
        return { valid: false, message: 'Event Date is Required' };
    }
    if (eventData.youtubeLink) {
        const match = eventData.youtubeLink.match(youtubeRegex);
        if (!match) {
            return { valid: false, message: "Oops! It looks like the YouTube link you've entered doesn't match the expected format. To ensure smooth processing, please use the 'Share' link directly from YouTube." };
        }
    }
    return { valid: true };
};
